import { openRivalRematch } from "../rivalry/rematch-navigation.js";
import {
  displaySides,
  displayRecord,
} from "../rivalry/comparison-presentation.js";
import { relationLabel } from "../rivalry/comparison-selection.js";
import { comparisonQuery } from "../rivalry/comparison-selection.js";
import {
  rivalInsightFilters,
  type RivalInsight,
} from "../../../shared/presentation/rival-insight.js";
import { InsightEvidence } from "../rivalry/insight-evidence.js";
import { useEffect, useState, type ReactNode } from "react";
import type {
  ComparisonDetail,
  ComparisonObservation,
} from "../../../shared/contracts/product-pages.js";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, X } from "../../shell/approved-icons.js";
import { ApprovedAvatar } from "../../shell/approved-primitives.js";
import { loadComparison } from "./clubhouse-comparison-api.js";
import {
  clubhouseAvatarId,
  type ClubhousePerson,
} from "./clubhouse-relations-model.js";
import { PersonRecord } from "./clubhouse-approved-cards.js";
import { useRivalryCalendar } from "../rivalry/use-rivalry-calendar.js";
import { comparisonAnalyticsLabel } from "../../../shared/presentation/rivalry-calendar.js";
import { teePairLabel } from "../rivalry/comparison-selection.js";

function ComparisonFrame(props: {
  open: boolean;
  title: string;
  description: string;
  onClose(): void;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <DialogContent className="fr-comparison-sheet" showCloseButton={false}>
        <header>
          <DialogTitle>{props.title}</DialogTitle>
          <button type="button" onClick={props.onClose} aria-label="Close">
            <X size={22} />
          </button>
        </header>
        <DialogDescription>{props.description}</DialogDescription>
        {props.children}
      </DialogContent>
    </Dialog>
  );
}

interface ComparisonDialogProps {
  relationId: string | null;
  person?: ClubhousePerson | null;
  finding?: RivalInsight | null;
  onClose(): void;
  onOpenSource(id: string): void;
  sourceIds?: string[];
  onStart?(): void;
  onOpenRecord?(id: string): void;
}

function useDialogComparison(props: ComparisonDialogProps) {
  const [resource, setResource] = useState<{
    key: string;
    detail: ComparisonDetail | null;
    error: boolean;
  }>({ key: "", detail: null, error: false });
  const [retry, setRetry] = useState(0);
  const calendar = useRivalryCalendar();
  const query = comparisonQuery(
    props.finding
      ? rivalInsightFilters(props.finding)
      : { basis: "gross", ...calendar.filters },
  );
  const key = `${props.relationId ?? ""}:${query}:${retry}`;
  const detail = resource.key === key ? resource.detail : null;
  const error = resource.key === key && resource.error;
  useEffect(() => {
    let current = true;
    setResource({ key, detail: null, error: false });
    if (props.relationId)
      void loadComparison(props.relationId, query)
        .then((value) => {
          if (current) setResource({ key, detail: value, error: false });
        })
        .catch(() => {
          if (current) setResource({ key, detail: null, error: true });
        });
    return () => {
      current = false;
    };
  }, [props.relationId, query, key]);
  return {
    detail,
    error,
    calendar,
    retry: () => setRetry((value) => value + 1),
  };
}

/** Canonical W08 evidence destination. Host owns opening the source scorecard. */
export function ComparisonDialog(
  props: ComparisonDialogProps,
): React.JSX.Element {
  const { detail, error, calendar, retry } = useDialogComparison(props);
  const title = detail
    ? relationLabel(detail.relation)
    : (props.person?.name ?? "Your rivalry");
  return (
    <ComparisonFrame
      open={Boolean(props.relationId || props.person)}
      title={title}
      description={
        props.finding
          ? comparisonAnalyticsLabel(props.finding.filters)
          : `${calendar.selection.ledger === "simulator" ? "Simulator" : "Main"} · ${calendar.filters.period === "year" ? calendar.filters.year : "All Time"}`
      }
      onClose={props.onClose}
    >
      {error ? (
        <div role="alert">
          <p>Your shared history could not load.</p>
          <Button variant="ghost" onClick={retry}>
            Try again
          </Button>
        </div>
      ) : props.relationId && !detail ? (
        <p role="status">Loading shared history…</p>
      ) : detail ? (
        <ComparisonContent
          detail={detail}
          finding={props.finding}
          sourceIds={props.sourceIds}
          onOpenSource={props.onOpenSource}
          onStart={props.onStart}
          onOpenRecord={props.onOpenRecord}
        />
      ) : (
        <div>
          <p>
            {props.person?.rival?.rounds
              ? "Your recorded rounds do not yet contain a qualified comparison between these parties."
              : "Your first shared round will start this rivalry."}
          </p>
          {props.onStart ? (
            <Button
              variant="gold-gradient"
              size="block"
              onClick={props.onStart}
            >
              Start a round
              <ArrowRight />
            </Button>
          ) : null}
        </div>
      )}
    </ComparisonFrame>
  );
}

interface ComparisonContentProps {
  detail: ComparisonDetail;
  finding?: RivalInsight | null | undefined;
  sourceIds?: string[];
  onOpenSource(id: string): void;
  onStart?: (() => void) | undefined;
  onOpenRecord?: ((id: string) => void) | undefined;
}

function ComparisonContent({
  detail,
  finding,
  sourceIds,
  onOpenSource,
  onStart,
  onOpenRecord,
}: ComparisonContentProps): React.JSX.Element {
  const [own, other] = displaySides(detail.relation);
  const left = detail.relation[own],
    right = detail.relation[other];
  const record = detail.record;
  const displayed = displayRecord(detail.relation, record, true);
  return (
    <div className="fr-comparison-content">
      {finding ? (
        <InsightEvidence
          finding={finding}
          detail={detail}
          onOpenRoundId={onOpenSource}
        />
      ) : null}
      <div className="fr-comparison-record">
        <span>
          {left.name}
          <strong>{displayed[0]!.wins}</strong>
        </span>
        <span>
          Draws<strong>{record.ties}</strong>
        </span>
        <span>
          {right.name}
          <strong>{displayed[1]!.wins}</strong>
        </span>
      </div>
      <p className="fr-comparison-note">
        {record.contestCount} agreed competitions in {record.roundCount} shared
        rounds. Main and agreed extra matches remain separate results.
        {startingNote(detail.relation.testRounds?.length)}
      </p>
      <Button
        variant="gold-gradient"
        size="block"
        onClick={() =>
          detail.sources.some((source) => source.state === "completed")
            ? openRivalRematch(detail)
            : onStart?.()
        }
      >
        {detail.sources.some((source) => source.state === "completed")
          ? "Play again · Rematch"
          : "Start a round"}
      </Button>
      <ComparisonPhases detail={detail} />
      {onOpenRecord ? (
        <Button
          variant="outline-gold"
          size="block"
          onClick={() => onOpenRecord(detail.relation.id)}
        >
          Full shared record · all stats
          <ArrowRight />
        </Button>
      ) : null}
      {detail.sources.length ? (
        <details>
          <summary>Source scorecards ({detail.sources.length})</summary>
          <ComparisonSources
            detail={detail}
            sourceIds={sourceIds}
            onOpenSource={onOpenSource}
          />
        </details>
      ) : null}
    </div>
  );
}

function ComparisonPhases({
  detail,
}: {
  detail: ComparisonDetail;
}): React.JSX.Element {
  const [own, other] = displaySides(detail.relation);
  const left = detail.relation[own],
    right = detail.relation[other];
  return (
    <>
      <h3>When do you take the lead?</h3>
      <p className="rc-analytics-basis">
        {comparisonAnalyticsLabel(detail.filters)}
      </p>
      <p className="fr-comparison-note">
        Gross strokes on paired, recorded holes. This lens does not change an
        agreed match result.
      </p>
      {!Object.values(detail.phases).some((value) => value.n > 0) ? (
        <p>
          Your first shared hole scores will reveal who starts stronger and who
          finishes best.
        </p>
      ) : null}
      {Object.entries(detail.phases)
        .filter(([, value]) => value.n > 0)
        .map(([key, value]) => (
          <PhaseRow
            key={key}
            label={
              {
                opening: "Opening hole",
                front9: "Front nine",
                back9: "Back nine",
                closing3: "Closing three",
              }[key] ?? key
            }
            observation={
              own === "left"
                ? value
                : {
                    ...value,
                    leftWins: value.rightWins,
                    rightWins: value.leftWins,
                  }
            }
            left={left.name}
            right={right.name}
          />
        ))}
    </>
  );
}

function PhaseRow({
  label,
  observation: value,
  left,
  right,
}: {
  label: string;
  observation: ComparisonObservation;
  left: string;
  right: string;
}): React.JSX.Element {
  return (
    <section className="fr-comparison-phase">
      <h4>{label}</h4>
      {value.n ? (
        <>
          <p>
            {left} {value.leftWins} · {right} {value.rightWins} · Tied{" "}
            {value.ties}
          </p>
          <small>
            {value.n} comparisons from {value.sampleRounds} rounds
            {value.smallSample ? " · Small sample" : ""}
            {value.missing ? ` · ${value.missing} incomplete` : ""}
          </small>
        </>
      ) : (
        <p>No complete paired evidence yet.</p>
      )}
    </section>
  );
}

function ComparisonSources({
  detail,
  sourceIds,
  onOpenSource,
}: {
  detail: ComparisonDetail;
  sourceIds?: string[];
  onOpenSource(id: string): void;
}): React.JSX.Element {
  return (
    <section>
      <h3>Source scorecards</h3>
      {detail.sources.map((source) => (
        <div className="fr-comparison-source" key={source.roundId}>
          <strong>{source.courseName}</strong>
          <small>{teePairLabel(source.teePair)}</small>
          {source.contests.map((contest) => (
            <p key={contest.id}>
              {contest.scope === "rivalry_only" ? "Extra match" : "Main result"}
              :{" "}
              {contest.winner === "tie"
                ? "Draw"
                : detail.relation[contest.winner].name}{" "}
              · {contest.label} · {contest.scoringBasis}
            </p>
          ))}
          <Button
            variant="link"
            disabled={
              sourceIds !== undefined && !sourceIds.includes(source.roundId)
            }
            onClick={() => onOpenSource(source.roundId)}
          >
            Open full scorecard
            <ArrowRight size={14} />
          </Button>
        </div>
      ))}
      {!detail.sources.length ? (
        <p>No qualified source scorecards yet.</p>
      ) : null}
    </section>
  );
}

export function ClubhousePeopleDialog(props: {
  open: boolean;
  people: ClubhousePerson[];
  onClose(): void;
  onSelect(person: ClubhousePerson): void;
  onAdd(): void;
}): React.JSX.Element {
  return (
    <ComparisonFrame
      open={props.open}
      title="Your rivals"
      description="Every person and every party you share the game with."
      onClose={props.onClose}
    >
      <div className="fr-home-people-sheet">
        {props.people.map((person) => (
          <button
            type="button"
            key={person.key}
            onClick={() => props.onSelect(person)}
          >
            <ApprovedAvatar
              name={person.name}
              participantId={clubhouseAvatarId(person)}
            />
            <span>
              <strong>{person.name}</strong>
              <PersonRecord person={person} />
            </span>
            <ArrowRight size={18} />
          </button>
        ))}
      </div>
      <Button variant="ghost" onClick={props.onAdd}>
        Add a rival
      </Button>
    </ComparisonFrame>
  );
}

function startingNote(count: number | undefined): string {
  return count
    ? ` Starting score includes ${count} test wins; statistics exclude them.`
    : "";
}
